import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Trash2, FileText, FileSpreadsheet, RefreshCw, Folder,
  Check, X, Eye, Download, ChevronUp, Copy, Info, Search, Table2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PDFViewer from "@/components/PDFViewer";
import FloatingWindow from "@/components/FloatingWindow";
import { copyToClipboard, topFrameDownload, parseCSV, parseExcel } from "@/lib/downloadUtils";

import { API_URL } from "@/lib/api";
const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const DownloadPanel = ({ filename, onClose }) => {
  const [copied, setCopied] = useState(false);
  const directUrl = `${BACKEND_URL}/api/reports/download/${filename}`;

  const handleCopy = async () => {
    const ok = await copyToClipboard(directUrl);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 3000); toast.success("URL copiada. Pegala en una nueva pestana de Chrome."); }
    else { toast.info("Selecciona el texto y copia con Ctrl+C"); }
  };

  return (
    <div className="border border-sky-200 rounded-lg bg-sky-50 p-4 space-y-4" data-testid="download-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Download className="w-4 h-4 text-[#FF9900]" /><span className="text-sm font-semibold text-foreground">Guardar archivo</span></div>
        <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-xs font-mono text-muted-foreground bg-card border rounded px-2 py-1 truncate">{filename}</p>

      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="bg-[#FF9900] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">A</span>
          <span className="text-xs font-semibold text-foreground">Elegir carpeta (recomendado)</span>
        </div>
        <p className="text-xs text-muted-foreground ml-6">Copia esta URL y pegala en una nueva pestana para el dialogo <strong>"Guardar como..."</strong>.</p>
        <div className="flex gap-2 ml-6">
          <input readOnly value={directUrl} onClick={e => e.target.select()}
            className="flex-1 text-xs font-mono bg-card border border-border rounded px-2 py-1.5 text-muted-foreground select-all"
            data-testid="direct-download-url" />
          <Button size="sm" onClick={handleCopy}
            className={`shrink-0 h-8 px-3 text-xs ${copied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#FF9900] hover:bg-[#E68A00]'} text-white`}
            data-testid="copy-url-button">
            {copied ? <><Check className="w-3 h-3 mr-1" />Copiada</> : <><Copy className="w-3 h-3 mr-1" />Copiar</>}
          </Button>
        </div>
        <div className="ml-6 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded p-2">
          <Info className="w-3 h-3 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">Chrome: <strong>Configuracion - Descargas - "Preguntar donde guardar"</strong></p>
        </div>
      </div>

      <div className="border-t border-sky-200" />

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="bg-emerald-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">B</span>
            <span className="text-xs font-semibold text-foreground">Descarga directa</span>
          </div>
          <p className="text-xs text-muted-foreground ml-6 mt-0.5"><span className="font-mono">C:\Users\[usuario]\Downloads\</span> - <kbd className="bg-muted rounded px-1 text-foreground">Ctrl+J</kbd></p>
        </div>
        <Button size="sm" onClick={() => { topFrameDownload(filename); toast.success(`Descargando: ${filename}`, { duration: 5000 }); }}
          className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs ml-3"
          data-testid="direct-download-button">
          <Download className="w-3 h-3 mr-1" />Descargar ahora
        </Button>
      </div>
    </div>
  );
};

const ViewerIcon = ({ type }) => {
  if (type === 'pdf') return <FileText className="w-4 h-4 text-rose-500" />;
  if (type === 'xlsx') return <Table2 className="w-4 h-4 text-[#FF9900]" />;
  return <FileSpreadsheet className="w-4 h-4 text-emerald-500" />;
};

const ReportIcon = ({ format }) => {
  if (format === 'pdf') return <FileText className="w-4 h-4 text-rose-500" />;
  if (format === 'xlsx') return <Table2 className="w-4 h-4 text-[#FF9900]" />;
  return <FileSpreadsheet className="w-4 h-4 text-emerald-500" />;
};

const getIconBg = (format) => {
  if (format === 'pdf') return 'bg-rose-50';
  if (format === 'xlsx') return 'bg-sky-50';
  return 'bg-emerald-50';
};

const ReportsExplorer = ({ open, onOpenChange }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [downloadPanelId, setDownloadPanelId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/reports/list`); setReports(r.data.reports); }
    catch { toast.error("Error al cargar los reportes"); }
    finally { setLoading(false); }
  }, []);

  const closeViewer = useCallback(() => {
    setViewer(prev => {
      if (prev?.blobUrl) window.URL.revokeObjectURL(prev.blobUrl);
      return null;
    });
  }, []);

  useEffect(() => {
    if (open) {
      fetchReports();
    } else {
      closeViewer();
      setDownloadPanelId(null);
      setSelected(new Set());
      setSearchTerm('');
      setTypeFilter('all');
      setBatchDeleteConfirm(false);
    }
  }, [open, fetchReports, closeViewer]);

  const filteredReports = useMemo(() => reports.filter(r => {
    const matchesType = typeFilter === 'all' || r.format === typeFilter;
    const matchesSearch = !searchTerm || r.filename.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  }), [reports, searchTerm, typeFilter]);

  const allFilteredSelected = filteredReports.length > 0 && filteredReports.every(r => selected.has(r.filename));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => { const n = new Set(prev); filteredReports.forEach(r => n.delete(r.filename)); return n; });
    } else {
      setSelected(prev => { const n = new Set(prev); filteredReports.forEach(r => n.add(r.filename)); return n; });
    }
  };

  const toggleSelect = (fn) => {
    setSelected(prev => { const n = new Set(prev); n.has(fn) ? n.delete(fn) : n.add(fn); return n; });
  };

  const handleView = async (report) => {
    if (viewer?.filename === report.filename) { closeViewer(); return; }
    closeViewer(); setDownloadPanelId(null);
    setBusyId(report.filename + '_view');
    try {
      const res = await axios.get(`${API}/reports/download/${report.filename}`, { responseType: 'blob' });
      if (report.format === 'pdf') {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        setViewer({ filename: report.filename, type: 'pdf', blobUrl: window.URL.createObjectURL(blob) });
      } else if (report.format === 'xlsx') {
        setViewer({ filename: report.filename, type: 'xlsx', csvRows: await parseExcel(res.data) });
      } else {
        setViewer({ filename: report.filename, type: 'csv', csvRows: parseCSV(await res.data.text()) });
      }
    } catch { toast.error("Error al cargar el archivo"); }
    finally { setBusyId(null); }
  };

  const handleDeleteOne = async (filename) => {
    try {
      await axios.delete(`${API}/reports/delete/${filename}`);
      toast.success(`Eliminado: ${filename}`);
      if (viewer?.filename === filename) closeViewer();
      if (downloadPanelId === filename) setDownloadPanelId(null);
      setSelected(prev => { const n = new Set(prev); n.delete(filename); return n; });
      setConfirmDeleteId(null);
      fetchReports();
    } catch { toast.error("Error al eliminar"); setConfirmDeleteId(null); }
  };

  const handleBatchDelete = async () => {
    const toDelete = [...selected];
    let deleted = 0;
    for (const fn of toDelete) {
      try {
        await axios.delete(`${API}/reports/delete/${fn}`);
        deleted++;
        if (viewer?.filename === fn) closeViewer();
        if (downloadPanelId === fn) setDownloadPanelId(null);
      } catch (err) {
        // Individual delete failed, continue with remaining
      }
    }
    setSelected(new Set()); setBatchDeleteConfirm(false);
    toast.success(`${deleted} reporte${deleted !== 1 ? 's' : ''} eliminado${deleted !== 1 ? 's' : ''}`);
    fetchReports();
  };

  const fmtSize = (b) => { if (!b) return '0 B'; const k=1024, s=['B','KB','MB'], i=Math.floor(Math.log(b)/Math.log(k)); return parseFloat((b/Math.pow(k,i)).toFixed(1))+' '+s[i]; };
  const fmtDate = (iso) => new Date(iso).toLocaleString('es-BO', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:false, timeZone:'America/La_Paz' });

  return (
    <FloatingWindow open={open} onClose={() => onOpenChange(false)} title="Explorador de Reportes">
      {/* Barra de busqueda y filtros */}
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, familia, fecha..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm" data-testid="reports-search-input" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center rounded-lg border border-border overflow-hidden h-8">
          {['all','pdf','xlsx','csv'].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} data-testid={`filter-type-${t}`}
              className={`px-3 h-full text-xs font-medium transition-colors ${typeFilter===t ? 'bg-[#FF9900] text-white' : 'text-muted-foreground hover:bg-muted'}`}>
              {t === 'all' ? 'Todos' : t.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">{filteredReports.length} de {reports.length}</span>
          <Button variant="outline" size="sm" onClick={fetchReports} disabled={loading} className="h-8" data-testid="refresh-reports-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Barra de acciones por lote */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 shrink-0" data-testid="batch-actions-bar">
          <span className="text-sm font-medium text-rose-700">{selected.size} reporte{selected.size!==1?'s':''} seleccionado{selected.size!==1?'s':''}</span>
          {batchDeleteConfirm ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-rose-700 font-medium">Eliminar {selected.size} reportes?</span>
              <Button size="sm" onClick={handleBatchDelete} className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-3 text-xs" data-testid="confirm-batch-delete-button">
                <Check className="w-3 h-3 mr-1" />Confirmar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setBatchDeleteConfirm(false)} className="h-7 px-2 text-muted-foreground" data-testid="cancel-batch-delete-button">
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-7 px-2 text-muted-foreground text-xs">Deseleccionar</Button>
              <Button size="sm" onClick={() => setBatchDeleteConfirm(true)} className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-3 text-xs" data-testid="batch-delete-button">
                <Trash2 className="w-3 h-3 mr-1" />Eliminar seleccionados
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Lista de reportes */}
      <div className="border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><RefreshCw className="w-7 h-7 animate-spin mx-auto text-[#FF9900] mb-2" /><p className="text-sm text-muted-foreground">Cargando...</p></div>
        ) : filteredReports.length === 0 ? (
          <div className="p-8 text-center"><Folder className="w-10 h-10 mx-auto text-slate-300 mb-2" /><p className="text-sm text-muted-foreground">{reports.length===0?'No hay reportes':'Sin coincidencias'}</p></div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-2 bg-muted border-b text-xs text-muted-foreground font-medium">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll}
                className="w-4 h-4 rounded accent-sky-600 cursor-pointer" data-testid="select-all-checkbox" title="Seleccionar todos" />
              <span>Nombre del archivo</span>
              <span className="ml-auto">Fecha - Tamano</span>
            </div>

            <div className="divide-y" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {filteredReports.map((report, index) => {
                const isViewing = viewer?.filename === report.filename;
                const isDlOpen = downloadPanelId === report.filename;
                const isChecked = selected.has(report.filename);

                return (
                  <div key={report.filename} data-testid={`report-item-${index}`}>
                    <div className={`px-4 py-2.5 flex items-center justify-between transition-colors ${isChecked?'bg-sky-50/80':isViewing||isDlOpen?'bg-sky-50':'hover:bg-muted'}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <input type="checkbox" checked={isChecked} onChange={() => toggleSelect(report.filename)}
                          className="w-4 h-4 rounded accent-sky-600 cursor-pointer shrink-0" data-testid={`checkbox-report-${index}`} />
                        <div className={`p-1.5 rounded-md shrink-0 ${getIconBg(report.format)}`}>
                          <ReportIcon format={report.format} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{report.filename}</p>
                          <p className="text-xs text-muted-foreground">{fmtDate(report.created_at)} - {fmtSize(report.size)}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        {confirmDeleteId === report.filename ? (
                          <div className="flex items-center gap-1 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-rose-700">Eliminar?</span>
                            <button onClick={() => handleDeleteOne(report.filename)} className="p-1 text-rose-600 hover:bg-rose-100 rounded" data-testid={`confirm-delete-yes-${index}`}><Check className="w-3 h-3" /></button>
                            <button onClick={() => setConfirmDeleteId(null)} className="p-1 text-muted-foreground hover:bg-muted rounded" data-testid={`confirm-delete-no-${index}`}><X className="w-3 h-3" /></button>
                          </div>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => { setDownloadPanelId(null); handleView(report); }}
                              disabled={busyId!==null}
                              className={`px-2 text-[#FF9900] hover:bg-[#FF9900]/10 ${isViewing?'bg-sky-100':''}`}
                              data-testid={`view-report-${index}`} title="Ver">
                              {busyId===report.filename+'_view' ? <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /> : isViewing ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button variant="outline" size="sm"
                              onClick={() => { closeViewer(); setDownloadPanelId(isDlOpen?null:report.filename); }}
                              disabled={busyId!==null}
                              className={`px-2 text-emerald-700 border-emerald-400 hover:bg-emerald-50 ${isDlOpen?'bg-emerald-50':''}`}
                              data-testid={`download-report-${index}`} title="Guardar">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(report.filename)}
                              disabled={busyId!==null} className="px-2 text-rose-500 hover:bg-rose-50"
                              data-testid={`delete-report-${index}`} title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {isDlOpen && (
                      <div className="px-4 pb-4 pt-1 bg-sky-50 border-t border-sky-100">
                        <DownloadPanel filename={report.filename} onClose={() => setDownloadPanelId(null)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Visor inline */}
      {viewer && (
        <div className="border rounded-lg overflow-hidden flex flex-col" style={{ minHeight: 320 }} data-testid="inline-viewer">
          <div className="flex items-center justify-between px-4 py-2 bg-muted border-b shrink-0">
            <div className="flex items-center gap-2">
              <ViewerIcon type={viewer.type} />
              <span className="text-sm font-medium text-foreground truncate max-w-sm">{viewer.filename}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { closeViewer(); setDownloadPanelId(viewer.filename); }}
                className="text-xs text-emerald-700 border-emerald-400 hover:bg-emerald-50 h-7 px-2" data-testid="viewer-download-button">
                <Download className="w-3 h-3 mr-1" />Guardar
              </Button>
              <Button variant="ghost" size="sm" onClick={closeViewer} className="h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
            </div>
          </div>

          {viewer.type === 'pdf' && (
            <div className="overflow-auto" style={{ maxHeight: 360 }}>
              <PDFViewer blobUrl={viewer.blobUrl} />
            </div>
          )}

          {(viewer.type === 'csv' || viewer.type === 'xlsx') && (
            <div className="overflow-auto" style={{ maxHeight: 360 }}>
              {viewer.csvRows?.length > 0 ? (
                <>
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr>{viewer.csvRows[0]?.map((h,i) => <th key={i} className="px-3 py-2 text-left font-semibold text-foreground bg-muted border-b border-r border-border whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {viewer.csvRows.slice(1,101).map((row,ri) => (
                        <tr key={ri} className={ri%2===0?'bg-card':'bg-muted'}>
                          {row.map((cell,ci) => <td key={ci} className="px-3 py-1.5 border-b border-r border-slate-100 whitespace-nowrap max-w-xs truncate">{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {viewer.csvRows.length > 102 && <div className="p-3 text-center bg-muted border-t"><p className="text-xs text-muted-foreground">Mostrando 100 de {viewer.csvRows.length-1} filas</p></div>}
                </>
              ) : <p className="p-4 text-sm text-muted-foreground">No se pudo leer el archivo.</p>}
            </div>
          )}
        </div>
      )}
    </FloatingWindow>
  );
};

export default ReportsExplorer;
